import { useState } from 'react';
import { COMPONENT_ART } from './componentArt.jsx';

/**
 * Show and Tell - the section that happens in the room.
 *
 * Everything else in this lab is on a screen. This one is for a teacher
 * holding the real parts up, because a CPU being surprisingly small and
 * heavy, or a RAM notch sitting off-centre, are things a diagram cannot
 * teach. A learner who has held these once recognises them for good.
 *
 * Two views of the same eight components:
 *
 *   Student  - what to look for as each part comes round, and a tick
 *              once it has actually been seen or held.
 *   Teacher  - a run sheet: the order to show them in, the one thing
 *              worth saying about each, a question to put to the room,
 *              and the safety notes.
 *
 * It works with nothing but the parts out of an old machine, which is
 * the point: no kit list, no budget.
 */

const SHOW = [
  {
    id: 'motherboard',
    name: 'Motherboard',
    look: 'Hold it up to the light at an angle. The faint lines all over it are copper tracks, and there are more layers of them sandwiched inside.',
    ask: 'Everything plugs into this one board. What happens to the machine if a single track cracks?',
    say: 'Start here so every other part has somewhere to belong. Point out the CPU socket, the memory slots and the long expansion slots as you go.',
  },
  {
    id: 'cpu',
    name: 'CPU',
    look: 'Smaller and heavier than everyone expects. Look for the gold contacts underneath and the tiny triangle in one corner.',
    ask: 'Why does it have a marked corner, when it looks square?',
    say: 'Pass it round in its plastic case if you have one. The triangle exists so it can only go in one way round - dropping it in rotated would bend hundreds of pins.',
  },
  {
    id: 'ram',
    name: 'RAM',
    look: 'A long thin stick with black chips along it. Find the notch in the gold contacts - it is deliberately not in the middle.',
    ask: 'The notch is off-centre. What is that for?',
    say: 'Let them try to work out the notch before telling them: it makes fitting it backwards physically impossible. Good hardware design makes the wrong thing not fit.',
  },
  {
    id: 'storage',
    name: 'Storage (SSD/HDD)',
    look: 'If you have both, compare the weight. Tilt the hard drive gently and you can feel the platters resist - an SSD has nothing moving at all.',
    ask: 'One of these has spinning parts and one does not. Which would you rather drop?',
    say: 'The weight difference makes the lesson land without a word. If you have a dead hard drive you are willing to sacrifice, opening it to show the platter and arm is the best five minutes of the session.',
  },
  {
    id: 'network',
    name: 'Network Adapter',
    look: 'Find the square socket on the end. Look closely and there are two tiny lights beside it that flicker when data moves.',
    ask: 'The cable clicks in and has to be squeezed to come out. Why bother with a clip?',
    say: 'Plug a cable in and out so they hear the click. Many will have seen this port on a router without knowing what it was.',
  },
  {
    id: 'psu',
    name: 'Power Supply (PSU)',
    look: 'The heaviest single part, with the biggest fan. Look at the wide connector: the holes are different shapes so it only fits one way.',
    ask: 'Every other part needs this one. What does that make it, for someone trying to take a machine offline?',
    say: 'Show it, do not open it. Say plainly that a power supply holds a charge even unplugged, and is the one part nobody opens.',
  },
  {
    id: 'gpu',
    name: 'Graphics Card',
    look: 'The biggest card, and heavy for its size - most of that weight is metal to carry heat away.',
    ask: 'Why does this one need its own fans when the network card does not?',
    say: 'Let them feel the weight against the network card. Heat is the giveaway: parts that work hard need somewhere to put it.',
  },
  {
    id: 'io',
    name: 'Input/Output',
    look: 'Turn the case round and look at the back panel. Count the different port shapes and see which ones you recognise.',
    ask: 'A stranger has thirty seconds alone with the back of this machine. What could they plug in?',
    say: 'Finish here, because it leads straight back to the lesson: these ports are the parts of a computer a person can physically reach.',
  },
];

const SAFETY = [
  'Unplug the machine at the wall and leave it a minute before opening anything.',
  'Touch bare metal on the case first, so any static goes there and not through a chip.',
  'Hold boards and cards by their edges. Skin oils on the gold contacts cause real faults.',
  'Never open a power supply, even unplugged. It stores a charge that can seriously hurt you.',
  'Nothing needs force. If a part will not go in, it is the wrong way round.',
];

export default function ShowAndTell({ onBack, onSolved }) {
  const [mode, setMode] = useState('student');
  const [seen, setSeen] = useState(new Set());

  const toggle = (id) => {
    setSeen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size === SHOW.length) onSolved?.();
      return next;
    });
  };

  /**
   * The run sheet is the only place to go inside this section, so back
   * returns from it before it leaves for the previous one. A teacher
   * who opened it mid-session should not lose the ticked list to do it.
   */
  const back = mode === 'teacher'
    ? { label: '← Back to the student view', act: () => setMode('student') }
    : { label: '← Back to Follow the Data', act: onBack };

  return (
    <section className="lab-section">
      <div className="place-head">
        <div>
          <h2>Show and Tell</h2>
          <p className="lab-lead">
            The part of this task that is not on a screen. Your teacher will show you each component
            for real — tick them off as they come round, and look for the details listed here.
          </p>
        </div>
        <div className="row gap-sm">
          <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
            {back.label}
          </button>
          <span className="pill">{seen.size} / {SHOW.length} seen</span>
        </div>
      </div>

      <div className="show-modes" role="tablist" aria-label="View">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'student'}
          className={`show-mode ${mode === 'student' ? 'is-active' : ''}`}
          onClick={() => setMode('student')}
        >
          🎒 For students
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'teacher'}
          className={`show-mode ${mode === 'teacher' ? 'is-active' : ''}`}
          onClick={() => setMode('teacher')}
        >
          🧑‍🏫 Teacher run sheet
        </button>
      </div>

      {/* ---------------- safety, first, always ---------------- */}
      <div className="show-safety">
        <strong>⚠️ Before anything is opened</strong>
        <ul>
          {SAFETY.map((s) => <li key={s}>{s}</li>)}
        </ul>
      </div>

      {/* ---------------- student view ---------------- */}
      {mode === 'student' && (
        <>
          <div className="show-grid">
            {SHOW.map((c) => {
              const Art = COMPONENT_ART[c.id];
              const ticked = seen.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`show-card ${ticked ? 'is-seen' : ''}`}
                  onClick={() => toggle(c.id)}
                  aria-pressed={ticked}
                >
                  <span className="show-tick" aria-hidden="true">{ticked ? '✓' : ''}</span>
                  <span className="show-art">{Art ? <Art /> : null}</span>
                  <strong>{c.name}</strong>
                  <p>{c.look}</p>
                  <span className="show-hint">{ticked ? 'Seen it' : 'Tap when you have seen it'}</span>
                </button>
              );
            })}
          </div>

          {seen.size === SHOW.length && (
            <div className="lab-done">
              <strong>All eight, in your hands.</strong> You will recognise these for good now — a
              diagram never quite manages that.
            </div>
          )}
        </>
      )}

      {/* ---------------- teacher view ---------------- */}
      {mode === 'teacher' && (
        <div className="show-runsheet">
          <p className="lab-lead">
            Roughly twenty minutes with parts from one old machine. Show them in this order so each
            one has somewhere to belong, and put the question to the room before you answer it.
          </p>

          {SHOW.map((c, i) => (
            <div key={c.id} className="show-step">
              <span className="show-step-n">{i + 1}</span>
              <div>
                <strong>{c.name}</strong>
                <p className="show-say">{c.say}</p>
                <p className="show-look"><em>Point out:</em> {c.look}</p>
                <p className="show-ask">💬 <strong>Ask the room:</strong> {c.ask}</p>
              </div>
            </div>
          ))}

          <div className="lab-note">
            <strong>If you have no spare parts</strong>
            <p>
              One dead desktop is enough, and most schools have one. Failing that, a phone taken
              apart in a repair video covers the same ground — the parts are the same, just smaller
              and soldered down. The point is that they have seen something real, not that the kit
              was impressive.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
